/**
 * Attribution tracking (GCID-Flow)
 * -----------------------------------------------------------------------------
 * Captures ad click IDs + marketing context and persists them (cookie 360d +
 * localStorage). The data is written into the cart as a single hidden attribute
 * `_attribution` (JSON) so it flows into the order, where a Shopify Flow copies
 * it into the order/customer metafield `custom.attribution`.
 *
 * Click IDs are stored as BOTH first- and latest-touch, each with its own
 * timestamp, so a long customer journey keeps the original acquisition click
 * AND the most recent one (e.g. swatch order via first click, sofa purchase via
 * a later click). Per click id `X` in {gclid, gbraid, wbraid, msclkid, ttclid,
 * fbclid}:
 *   first_X, first_X_timestamp   -> set once, NEVER overwritten
 *   latest_X, latest_X_timestamp -> updated whenever a new (different) value appears
 * UTMs and landing_page/referrer/timestamp remain first-touch (first-session context).
 *
 * On order creation, whatever values exist at that moment are frozen as a
 * per-order snapshot in the order metafield (repeat orders keep their own value).
 *
 * Re-synced to the cart on: page load (if a cart exists), add-to-cart /
 * cart-update (`cart:change`), and checkout click.
 *
 * IMPORTANT: this snippet must be included in <head> WITHOUT `defer`/`async`,
 * and BEFORE the theme's product-page redirect script (theme.liquid, template
 * "product" — redirects via metafields `redirect.product_page` /
 * `card.preselect_variant`). That redirect runs `window.location.href =` as a
 * synchronous inline <script> during head parsing, i.e. before
 * DOMContentLoaded ever fires, and its target URL does not carry over the
 * incoming query string — so any click ID/UTM in the URL is lost before a
 * DOMContentLoaded-gated capture would ever run. `capture()` therefore runs
 * synchronously at the top level below, not inside the DOMContentLoaded-gated
 * init(); only cart-sync (fetch/event listeners) waits for DOM readiness.
 *
 * IMPORTANT: Shopify's `/cart/update.js` REPLACES the entire cart `attributes`
 * object with whatever is sent — it does not merge. Other apps/scripts on this
 * store (observed: keys like `cart_id`, `__wc_client`, `__wc_client_current`,
 * `__wc_visitor`) also write cart attributes the same way. Sending only
 * `{ _attribution: ... }` would wipe out their keys, and — since whichever
 * write lands last on Shopify's server wins — their write can just as easily
 * wipe out ours. Every write here therefore merges onto the most recently
 * observed attributes (`knownCartAttributes`) instead of replacing wholesale.
 * This does not eliminate the race (we can't control other apps' code), but it
 * stops us from being a cause of it.
 *
 * Exposed as `window.upliftsAttribution` for debugging / future use.
 */
(function () {
  'use strict';

  var STORAGE_KEY = '_uplifts_attribution';
  // Target retention. NOTE: Safari/iOS (ITP) caps JS-writable storage (cookies set via
  // document.cookie AND localStorage) to ~7 days regardless of this value. The full
  // window only applies to Chrome/Firefox/Edge. Durable long-window retention on Safari
  // would require a server-set (HttpOnly) cookie, i.e. a backend — out of scope here.
  var COOKIE_DAYS = 360;

  // Ad platform click IDs: Google, Microsoft/Bing, TikTok, Meta/Facebook.
  var CLICK_IDS = ['gclid', 'gbraid', 'wbraid', 'msclkid', 'ttclid', 'fbclid'];
  var UTMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

  var lastSyncedPayload = null;
  // Best-known snapshot of the cart's OTHER attributes (owned by other apps),
  // refreshed whenever we observe live cart data. Used so our writes merge
  // instead of replacing — see file header.
  var knownCartAttributes = {};

  /* ------------------------------------------------------------------ utils */

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, days) {
    var expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie =
      name + '=' + encodeURIComponent(value) +
      '; expires=' + expires + '; path=/; SameSite=Lax';
  }

  function readStore() {
    try {
      var ls = window.localStorage.getItem(STORAGE_KEY);
      if (ls) return JSON.parse(ls);
    } catch (e) { /* localStorage blocked */ }

    var cookie = getCookie(STORAGE_KEY);
    if (cookie) {
      try { return JSON.parse(cookie); } catch (e) { /* corrupt */ }
    }
    return {};
  }

  function writeStore(data) {
    var json = JSON.stringify(data);
    try { window.localStorage.setItem(STORAGE_KEY, json); } catch (e) { /* ignore */ }
    setCookie(STORAGE_KEY, json, COOKIE_DAYS);
  }

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* --------------------------------------------------------------- capture */

  function capture() {
    var params = new URLSearchParams(window.location.search);
    var stored = readStore();
    var now = new Date().toISOString();
    var changed = false;

    // Click IDs: keep first-touch immutable AND track the latest touch, each
    // with its own timestamp.
    CLICK_IDS.forEach(function (key) {
      var value = params.get(key);
      if (!value) return;

      // First-touch: set once, never overwrite.
      if (!stored['first_' + key]) {
        stored['first_' + key] = value;
        stored['first_' + key + '_timestamp'] = now;
        changed = true;
      }

      // Latest-touch: update only when a genuinely new (different) value appears.
      if (stored['latest_' + key] !== value) {
        stored['latest_' + key] = value;
        stored['latest_' + key + '_timestamp'] = now;
        changed = true;
      }
    });

    // UTMs stay first-touch (campaign context of the first session).
    UTMS.forEach(function (key) {
      var value = params.get(key);
      if (value && !stored[key]) {
        stored[key] = value;
        changed = true;
      }
    });

    // Landing page, referrer and timestamp are frozen on the very first visit.
    if (!stored.timestamp) {
      stored.landing_page = window.location.href;
      stored.referrer = document.referrer || '';
      stored.timestamp = now;
      changed = true;
    }

    if (changed) writeStore(stored);
    return stored;
  }

  /* --------------------------------------------------------------- cart sync */

  function payloadFor(data) {
    return JSON.stringify(data);
  }

  // Merge our key onto the most recently observed attributes rather than
  // replacing the whole object (see file header — /cart/update.js replaces).
  function mergedAttributes(payload) {
    var merged = {};
    for (var key in knownCartAttributes) {
      if (Object.prototype.hasOwnProperty.call(knownCartAttributes, key)) {
        merged[key] = knownCartAttributes[key];
      }
    }
    merged._attribution = payload;
    return merged;
  }

  function postAttributes(payload) {
    if (payload === lastSyncedPayload) return; // avoid redundant writes
    lastSyncedPayload = payload;

    fetch(cartRoot() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: mergedAttributes(payload) }),
      keepalive: true
    }).catch(function () {
      lastSyncedPayload = null; // allow a retry on the next trigger
    });
  }

  // Write only when the cart doesn't already carry the correct value.
  function syncFromCart(cart, data) {
    var payload = payloadFor(data);
    if (cart && cart.attributes) knownCartAttributes = cart.attributes;
    var current = cart && cart.attributes && cart.attributes._attribution;
    if (current === payload) {
      lastSyncedPayload = payload;
      return;
    }
    postAttributes(payload);
  }

  /* ---------------------------------------------------------------- triggers */

  // Runs synchronously the instant this script executes — see the file header
  // for why this must not be deferred to DOMContentLoaded.
  var capturedData = capture();

  function initCartSync() {
    // On load: attach attribution to an existing cart (don't create empty carts).
    fetch(cartRoot() + 'cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart && cart.item_count > 0) syncFromCart(cart, capturedData);
      })
      .catch(function () { /* ignore */ });

    // Add-to-cart / cart-update: theme dispatches `cart:change` with the cart.
    document.addEventListener('cart:change', function (event) {
      var current = readStore();
      var cart = event.detail && event.detail.cart;
      syncFromCart(cart, current);
    });

    // Checkout click: fire-and-forget write before navigation (keepalive).
    document.addEventListener('click', function (event) {
      if (!event.target || !event.target.closest) return;
      var target = event.target.closest(
        '[name="checkout"], [data-checkout], a[href$="/checkout"], a[href*="/checkout?"]'
      );
      if (target) postAttributes(payloadFor(readStore()));
    }, true);
  }

  // Public accessor (used for debugging and a possible sign-up attribution layer).
  window.upliftsAttribution = {
    get: function () { return readStore(); }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartSync);
  } else {
    initCartSync();
  }
})();
